'use client'

import { Truncate } from '@re-dev/react-truncate'
import clsx from 'clsx'
import { Ingredient } from 'cms-types'
import Link from 'next/link'
import { useCallback, useDeferredValue, useMemo, useRef, useState } from 'react'
import { AccordionList, Icon, Pagination, PayloadImage, sanitizeHtmlId } from 'ui'
import { AccordionListHandles } from 'ui/components/AccordionList'
import { usePagination } from 'ui/hooks/usePagination'

import { pageToUrl, resolveRelation } from '@/cms/helpers'
import { richTextToJSX } from '@/helpers/richTextParser'
import { useClientSearchParams } from '@/hooks/useClientSearchParams'
import { useUrlValidation } from '@/hooks/useUrlValidation'
import { useGlobalTypography } from '@/store/globalTypographyStore'

import { useIngredientsPageSearchQuery } from './store'

type IngredientsListClientProps = {
  ingredients: Ingredient[]
  importantIngredients: Ingredient[]
}

export default function IngredientsListClient({
  ingredients,
  importantIngredients,
}: IngredientsListClientProps) {
  const accordionHandles = useRef<AccordionListHandles>(null)

  const {
    blogPostLinkTitle,
    ingredientPageLinkTitle,
    importantIngredientsTitle,
    restIngredientsTitle,
    maxItemsPerPage,
  } = useGlobalTypography(state => state.ingredientsPage)
  const linkTitles = useMemo(
    () => ({ blogPost: blogPostLinkTitle, ingredientPage: ingredientPageLinkTitle }),
    [blogPostLinkTitle, ingredientPageLinkTitle],
  )

  const [page, setPage] = useState(1)

  const { searchQuery } = useIngredientsPageSearchQuery()

  const deferredQuery = useDeferredValue(searchQuery)

  const filteredImportantIngredients = useMemo(() => {
    const normalizedQuery = deferredQuery.toLowerCase()

    return importantIngredients.filter(({ name }) => name.toLowerCase().includes(normalizedQuery))
  }, [deferredQuery, importantIngredients])

  const filteredIngredientsPanels = useMemo(() => {
    const normalizedQuery = deferredQuery.toLowerCase()

    return ingredients
      .filter(({ name }) => name.toLowerCase().includes(normalizedQuery))
      .map(({ name, subtitle, description, page, previewImage, slug }) => ({
        title: name,
        subtitle,
        content: [
          <div className='flex flex-col gap-8' key={1}>
            <div className='flex flex-col-reverse items-center gap-10 sm:items-start lg:flex-row lg:items-center'>
              <div className='flex flex-col gap-8'>
                {richTextToJSX(description, { textOnly: true })}
              </div>

              <div className='relative h-[233px] w-full max-w-[366px] shrink-0 overflow-hidden rounded-md xl:!w-[366px]'>
                <PayloadImage sizes='366px' objectFit='cover' src={resolveRelation(previewImage)} />
              </div>
            </div>

            <IngredientLinks
              slug={slug}
              page={page}
              linkTitles={linkTitles}
              className='focus-visible:-outline-offset-1 lg:pb-4'
            />
          </div>,
        ],
      }))
  }, [linkTitles, deferredQuery, ingredients])

  const { pagination, totalPages, dataToShow } = usePagination(filteredIngredientsPanels, {
    page,
    maxItemsPerPage,
  })

  const onPageChange = useCallback(
    (newPage: number) => {
      setPage(newPage)
      const currentUrl = new URL(location.href)
      const newSearchParams = new URLSearchParams(currentUrl.search)

      if (pagination.length === 1) {
        newSearchParams.delete('page')
      } else {
        newSearchParams.set('page', newPage.toString())
      }

      const searchParams = newSearchParams.toString()

      history.replaceState(
        null,
        '',
        `${location.pathname}${searchParams.length === 0 ? '' : `?${searchParams}`}`,
      )
    },
    [pagination.length],
  )

  const clientSearchParams = useClientSearchParams()

  useUrlValidation({
    searchParams: clientSearchParams,
    searchParamsCheckRules: [
      {
        key: 'page',
        testValue(value) {
          return !Number.isNaN(value) && Math.min(Math.max(+value, 0), pagination.length) === +value
        },
        onSuccess: page => {
          setPage(+page)
        },
      },
    ],
  })

  return (
    <>
      {filteredImportantIngredients.length !== 0 && (
        <div className='py-5 xl:py-10'>
          <h4 className='h4 pb-8 text-center xl:pb-16'>{importantIngredientsTitle}</h4>

          <ul className='isolate flex flex-wrap justify-center gap-5'>
            {filteredImportantIngredients.map((ingredient, index) => (
              <ImportantIngredientCard
                ingredient={ingredient}
                key={index}
                linkTitles={linkTitles}
                accordionHandles={accordionHandles}
              />
            ))}
          </ul>
        </div>
      )}

      <div className='mx-auto max-w-[904px]'>
        {filteredImportantIngredients.length !== 0 && dataToShow.length !== 0 && (
          <h4 className='h4 pb-8 text-center xl:pb-16'>{`${dataToShow.length}${restIngredientsTitle}`}</h4>
        )}

        <AccordionList ref={accordionHandles} shouldTriggerNavigation panels={dataToShow} />

        {pagination.length !== 1 && (
          <div className='mx-auto w-max pt-10 xl:pt-20'>
            <Pagination
              page={page}
              pagination={pagination}
              totalPages={totalPages}
              setPage={onPageChange}
            />
          </div>
        )}
      </div>
    </>
  )
}

type LinkTitles = { blogPost: string; ingredientPage: string }

/** Links to the ingredient's own page and, if set, to the related blog post */
function IngredientLinks({
  slug,
  page,
  linkTitles,
  className,
}: Pick<Ingredient, 'slug' | 'page'> & { linkTitles: LinkTitles; className?: string }) {
  const links = [
    slug ? { url: `/inhaltsstoff/${slug}`, title: linkTitles.ingredientPage } : null,
    page != null ? { url: pageToUrl(page) ?? '', title: linkTitles.blogPost } : null,
  ].filter(link => link != null)

  if (links.length === 0) return null

  return (
    <div className='flex flex-wrap gap-x-6 gap-y-2'>
      {links.map(({ url, title }) => (
        <Link
          key={url}
          href={url}
          className={clsx(
            'w-max no-underline transition-colors duration-300 hover:text-darkgray',
            className,
          )}
        >
          <p className='underline underline-offset-8'>{title}</p>
        </Link>
      ))}
    </div>
  )
}

type ImportantIngredientCardProps = {
  ingredient: Ingredient
  linkTitles: LinkTitles
  accordionHandles: React.RefObject<AccordionListHandles | null>
}

function ImportantIngredientCard({
  ingredient: { name, subtitle, description, previewImage, page, slug },
  linkTitles,
  accordionHandles,
}: ImportantIngredientCardProps) {
  const [truncateLoaded, setTruncateLoaded] = useState(false)
  const descriptionContent = useMemo(
    () =>
      richTextToJSX(description, {
        textOnly: true,
      }),
    [description],
  )

  return (
    <li className='relative h-[600px] w-[440px] overflow-hidden rounded-md'>
      <PayloadImage sizes='440px' objectFit='cover' src={resolveRelation(previewImage)} />

      <div className='absolute bottom-5 left-5 right-5 flex h-[400px] flex-col justify-between overflow-hidden rounded-sm bg-white p-6'>
        <div className='flex max-h-[300px] flex-col gap-7'>
          <div className='flex flex-col gap-3'>
            <h4 className='h4'>
              {slug ? (
                <Link
                  href={`/inhaltsstoff/${slug}`}
                  className='no-underline transition-colors duration-300 hover:text-darkgray'
                >
                  {name}
                </Link>
              ) : (
                name
              )}
            </h4>

            <h6 className='h6'>{subtitle}</h6>
          </div>

          <div className='p relative'>
            <Truncate
              lines={3}
              className={clsx('absolute w-full', !truncateLoaded && 'invisible')}
              ellipsis={
                <span>
                  ...{' '}
                  <button
                    onClick={() => {
                      accordionHandles.current?.setOpenAccordionByTitle(sanitizeHtmlId(name))
                    }}
                    className='inline-block no-underline'
                  >
                    <Icon size={16} name='MdChevronRight' className='rotate-90' />
                  </button>
                </span>
              }
              onTruncate={() => {
                setTruncateLoaded(true)
              }}
            >
              {descriptionContent}
            </Truncate>
          </div>
          {/* Fake content to take container height */}
          <div className='invisible line-clamp-3 overflow-hidden'>{descriptionContent}</div>
        </div>
        <IngredientLinks slug={slug} page={page} linkTitles={linkTitles} />
      </div>
    </li>
  )
}
